import React, { useContext } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import WorkOrdersContext, {
  FilterSection,
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { Icon, IconButton, TextField } from '@atom/mui';
import {
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  PlaylistAddCheckIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { TaskFilter } from '@atom/types/work';
import { WorkDatePresetKey } from '@atom/types/workFilters';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  convertRangeDateToMillis,
  DateRange,
  DateRangePresetOption,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import WorkOrdersTaskCustomFieldsFilter from './WorkOrdersTaskCustomFieldsFilter';
import WorkOrdersTaskFieldsFilter from './WorkOrdersTaskFieldsFilter';

import classes from './workOrdersFilters.module.css';

const DAY_IN_MILLISECONDS = 8.64e7;

const styles = {
  icon: {
    marginRight: '1rem',
  },
  separator: {
    margin: '0 1rem',
    paddingBottom: '0.5rem',
  },
};

const WorkOrdersFiltersTaskSection = () => {
  const {
    collapsedSections,
    setCollapsedSections,
    workOrdersInputCart,
    customFieldFilters,
    filtersDisabled,
    dispatch,
  } = useContext(WorkOrdersContext);

  const { dateFilterPresetsCart } = workOrdersInputCart;

  const onToggle = (section: FilterSection) => {
    setCollapsedSections(toggleFromSet(collapsedSections, section));
  };

  const handleUpdate = (value: any, property: keyof TaskFilter) => {
    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_TASKS_INPUT_PROPERTY,
      data: {
        property,
        value,
      },
    });
  };

  const handleDateChange =
    (
      property: keyof TaskFilter,
      terminator: DateRangeTerminator,
      preset?: DateRange | string,
    ) =>
    (val: Date) => {
      handleUpdate(convertRangeDateToMillis(val, terminator, preset), property);
    };

  const handleDatePresetChange =
    (type: WorkDatePresetKey, property: string) =>
    (option: DateRangePresetOption) => {
      const newDateFilterPresets = {
        ...dateFilterPresetsCart,
        [type]: {
          ...dateFilterPresetsCart[type],
          [property]: option.value,
        },
      };

      dispatch({
        type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
        data: {
          property: 'dateFilterPresetsCart',
          value: newDateFilterPresets,
        },
      });
    };

  const handleDurationChange =
    (property: keyof TaskFilter) => (event: React.ChangeEvent<any>) => {
      const value =
        event.target.value === ''
          ? null
          : Number(event.target.value) * DAY_IN_MILLISECONDS;

      handleUpdate(value, property);
    };

  const getDateValue = (property: keyof TaskFilter) => {
    if (!workOrdersInputCart || !workOrdersInputCart.tasks) {
      return null;
    }

    return workOrdersInputCart.tasks[property]
      ? // @ts-ignore
        new Date(workOrdersInputCart.tasks[property])
      : null;
  };

  const convertMillisecondsToDays = (daysMillis?: number) => {
    return daysMillis ? daysMillis / DAY_IN_MILLISECONDS : daysMillis;
  };

  return (
    <div className={classes.filtersSection}>
      <div className={classes.filtersSectionHeader}>
        <div>
          <Icon style={styles.icon} icon={PlaylistAddCheckIcon} />
          Task
        </div>
        <IconButton onClick={() => onToggle(FilterSection.TASK)} edge="end">
          <Icon
            icon={
              collapsedSections.has(FilterSection.TASK)
                ? KeyboardArrowDownIcon
                : KeyboardArrowUpIcon
            }
          />
        </IconButton>
      </div>
      {!collapsedSections.has(FilterSection.TASK) && (
        <div className={classes.filtersSectionContent}>
          {!workOrdersInputCart?.withoutDueDate && (
            <div className={classes.filterContainer}>
              <InputDateRange
                label="Due Date"
                disabled={filtersDisabled}
                presetSelected={
                  dateFilterPresetsCart?.[WorkDatePresetKey.TASK]?.dueDatePreset
                }
                startDateValue={getDateValue('dueDateStart')}
                handleStartChange={handleDateChange(
                  'dueDateStart',
                  DateRangeTerminator.START,
                  dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                    ?.dueDatePreset,
                )}
                endDateValue={getDateValue('dueDateEnd')}
                handleEndChange={handleDateChange(
                  'dueDateEnd',
                  DateRangeTerminator.END,
                  dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                    ?.dueDatePreset,
                )}
                handleSelectDatePreset={handleDatePresetChange(
                  WorkDatePresetKey.TASK,
                  'dueDatePreset',
                )}
              />
            </div>
          )}
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Start Date"
              disabled={filtersDisabled}
              presetSelected={
                dateFilterPresetsCart?.[WorkDatePresetKey.TASK]?.startDatePreset
              }
              startDateValue={getDateValue('startDateStart')}
              handleStartChange={handleDateChange(
                'startDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                  ?.startDatePreset,
              )}
              endDateValue={getDateValue('startDateEnd')}
              handleEndChange={handleDateChange(
                'startDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                  ?.startDatePreset,
              )}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.TASK,
                'startDatePreset',
              )}
            />
          </div>
          <div className={classes.filterContainer}>
            <InputDateRange
              label="Completed Date"
              disabled={filtersDisabled}
              presetSelected={
                dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                  ?.completionDatePreset
              }
              startDateValue={getDateValue('completionDateStart')}
              handleStartChange={handleDateChange(
                'completionDateStart',
                DateRangeTerminator.START,
                dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                  ?.completionDatePreset,
              )}
              endDateValue={getDateValue('completionDateEnd')}
              handleEndChange={handleDateChange(
                'completionDateEnd',
                DateRangeTerminator.END,
                dateFilterPresetsCart?.[WorkDatePresetKey.TASK]
                  ?.completionDatePreset,
              )}
              handleSelectDatePreset={handleDatePresetChange(
                WorkDatePresetKey.TASK,
                'completionDatePreset',
              )}
            />
          </div>
          <div
            className={clsx(classes.filtersDateRange, classes.filterContainer)}
          >
            <TextField
              value={convertMillisecondsToDays(
                workOrdersInputCart?.tasks?.durationStart,
              )}
              onChange={handleDurationChange('durationStart')}
              label="Duration"
              type="number"
              disabled={filtersDisabled}
              variant="standard"
              inputLabelColor={colors.neutral.dim}
            />
            <span style={styles.separator}>to</span>
            <TextField
              value={convertMillisecondsToDays(
                workOrdersInputCart?.tasks?.durationEnd,
              )}
              onChange={handleDurationChange('durationEnd')}
              label=""
              type="number"
              disabled={filtersDisabled}
              variant="standard"
            />
            <span style={styles.separator}>Days</span>
          </div>
          {!isNilOrEmpty(customFieldFilters) && (
            <div className={classes.filterContainer}>
              <WorkOrdersTaskCustomFieldsFilter />
            </div>
          )}
          {R.length(workOrdersInputCart?.workTemplateIds) === 1 && (
            <div className={classes.filterContainer}>
              <WorkOrdersTaskFieldsFilter />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkOrdersFiltersTaskSection;
